import API from "./axios";

const makeCrud = (base) => ({
  getAll:   ()           => API.get(`/${base}`),
  /**
   * Server-side search, one page at a time.
   *
   * Masters such as materials run to thousands of rows, and a dropdown that
   * calls getAll() downloads every one of them before it can open. This asks
   * the backend for the first `size` matches instead, so the list opens at the
   * same speed whether the master holds fifty rows or fifty thousand.
   */
  search:   (q, size = 10) => API.get(`/${base}/paged`, {
    params: { q: q || "", size, page: 1, isActive: true },
  }),
  getById:  (id)         => API.get(`/${base}/${id}`),
  create:   (data)       => API.post(`/${base}`, data),
  update:   (id, data)   => API.put(`/${base}/${id}`, data),
  toggle:   (id)         => API.patch(`/${base}/${id}/toggle-status`),
  delete:   (id)         => API.delete(`/${base}/${id}`),
});

export const firmApi = {
  getAll:  ()           => API.get("/firms"),
  getById: (id)         => API.get(`/firms/${id}`),
  create:  (data)       => API.post("/firms", data),
  update:  (id, data)   => API.put(`/firms/${id}`, data),
  toggle:  (id)         => API.patch(`/firms/${id}/toggle-status`),
  delete:  (id)         => API.delete(`/firms/${id}`),
};

// LocationController now returns ApiResponse-wrapped responses
export const locationApi = {
  getAll:  ()           => API.get("/locations"),
  getById: (id)         => API.get(`/locations/${id}`),
  create:  (data)       => API.post("/locations", data),
  update:  (id, data)   => API.put(`/locations/${id}`, data),
  toggle:  (id)         => API.patch(`/locations/${id}/toggle-status`),
  delete:  (id)         => API.delete(`/locations/${id}`),
  // The locations this login works in: { all, defaultLocationId, mainLocationId, locations }.
  mine:    ()           => API.get("/locations/mine"),
  setMain: (id)         => API.patch(`/locations/${id}/main`),
};

export const uomApi      = makeCrud("uom");
// getAll() returns panna widths AND GSMs (each row has measureType PANNA /
// GSM) so a cloth line can show either; the Panna master uses pannaWidthApi.
export const pannaApi    = makeCrud("pannas");
export const pannaWidthApi = {
  ...makeCrud("pannas"),
  getAll: () => API.get("/pannas", { params: { type: "PANNA" } }),
};
// GSM master — fabric weights for kg cloth (rows of the panna table).
export const gsmApi      = makeCrud("gsms");
export const matchingApi = makeCrud("matchings");
export const colorApi = {
  ...makeCrud("colors"),
  getActive: ()          => API.get("/colors/active"),
  search:    (q)         => API.get(`/colors/search?q=${encodeURIComponent(q || "")}`),
};
export const brandApi = {
  getAll:    ()         => API.get("/brands"),
  getActive: ()         => API.get("/brands/active"),
  getById:   (id)       => API.get(`/brands/${id}`),
  create:    (data)     => API.post("/brands", data),
  update:    (id, data) => API.put(`/brands/${id}`, data),
  toggle:    (id)       => API.patch(`/brands/${id}/toggle`),
  delete:    (id)       => API.delete(`/brands/${id}`),
};

export const rackApi     = {
  ...makeCrud("racks"),
  getByLocation: (locationId) => API.get(`/racks/location/${locationId}`),
};

export const itemApi = {
  ...makeCrud("items"),
  /** Phase 5 — paged register. params: { page, size, sortBy, sortDir, q, isActive } */
  paged:      (params) => API.get("/items/paged", { params }),
  addSize:    (itemId, data) => API.post(`/items/${itemId}/sizes`, data),
  deleteSize: (itemId, sizeId) => API.delete(`/items/${itemId}/sizes/${sizeId}`),
};

export const clothApi = {
  ...makeCrud("cloths"),
  /** Phase 5 — paged register. params: { page, size, sortBy, sortDir, q, isActive } */
  paged:        (params)       => API.get("/cloths/paged", { params }),
  getActive:    ()             => API.get("/cloths/active"),
  getByPannas:  (pannaIds)     => API.get(`/cloths/by-pannas?${pannaIds.map(id => `pannaIds=${id}`).join("&")}`),
};

export const materialApi = makeCrud("materials");

export const vendorAgentApi = {
  getAll:        ()              => API.get("/vendor-agents"),
  getByVendor:   (vendorId)     => API.get(`/vendor-agents/vendor/${vendorId}`),
  getActive:     (vendorId)     => API.get(`/vendor-agents/vendor/${vendorId}/active`),
  getById:       (id)           => API.get(`/vendor-agents/${id}`),
  create:        (data)         => API.post("/vendor-agents", data),
  update:        (id, data)     => API.put(`/vendor-agents/${id}`, data),
  toggle:        (id)           => API.patch(`/vendor-agents/${id}/toggle-status`),
  delete:        (id)           => API.delete(`/vendor-agents/${id}`),
};

export const customerGroupApi = {
  getAll:   ()           => API.get("/customer-groups"),
  getActive:()           => API.get("/customer-groups/active"),
  getById:  (id)         => API.get(`/customer-groups/${id}`),
  create:   (data)       => API.post("/customer-groups", data),
  update:   (id, data)   => API.put(`/customer-groups/${id}`, data),
  toggle:   (id)         => API.patch(`/customer-groups/${id}/toggle-status`),
  delete:   (id)         => API.delete(`/customer-groups/${id}`),
};

export const patternApi = {
  ...makeCrud("patterns"),
  // Phase 36da.99.fi — fetch only the patterns referenced by a view
  // (plus their source basic patterns).  Avoids pulling every pattern.
  byIds:     (ids)    => API.get("/patterns/by-ids", { params: { ids: (ids || []).join(",") } }),
  getActive: ()        => API.get("/patterns?active=true"),
  search:    (q)       => API.get(`/patterns/search?q=${encodeURIComponent(q || "")}`),
  // Phase 5 — server-side pagination + sort + search.
  // params: { page, size, sortBy, sortDir, q, isActive, itemId }
  paged:     (params = {}) => API.get("/patterns/paged", { params }),
  exportExcel: (id) => API.get(`/patterns/${id}/export/excel`, { responseType: "blob" }),
  exportPdf:   (id) => API.get(`/patterns/${id}/export/pdf`,   { responseType: "blob" }),
};

// Build full URL for uploaded files (images etc.)
export const getFileUrl = (path) => {
  if (!path) return null;
  // API base is e.g. "https://mukulapi.solapurmall.com/api" or "http://localhost:8908/api"
  // File endpoint is /api/files/... so we just append to the same base without the /api suffix
  const apiBase = process.env.REACT_APP_API_BASE_URL || "https://mukulapi.solapurmall.com/api";
  const serverRoot = apiBase.endsWith("/api")
    ? apiBase.slice(0, -4)          // strip trailing /api
    : apiBase.replace(/\/api\/.*$/, ""); // strip /api/... suffix
  return `${serverRoot}/api/files/${path}`;
};

export const productApi = {
  ...makeCrud("products"),
  getActive:     ()          => API.get("/products/active"),
  // SLIM list for dropdowns — no itemSizes / sizeCostBreakdowns / materials,
  // and no per-product query on the server. getAll (from makeCrud) returns the
  // full costing graph and should only be used where that is genuinely needed.
  getAllSummary: ()          => API.get("/products/summary"),
  getNextCode:   ()          => API.get("/products/next-code"),
  getByPattern:  (patternId) => API.get(`/products/pattern/${patternId}`),
  search:        (keyword)   => API.get(`/products/search?keyword=${encodeURIComponent(keyword)}`),
  // Phase 5 — server-side pagination + sort + search.
  // params: { page, size, sortBy, sortDir, q, isActive, patternId }
  paged:         (params = {}) => API.get("/products/paged", { params }),
  calcPricing:   (data)      => API.post("/products/calc-pricing", data),
  // The existing product with exactly this pattern + cloth/panna per matching
  // + services, or null. body: { patternId, matchings:[{matchingId, clothId,
  // clothVariantId, pannaId}], serviceIds }
  findMatch:     (data)      => API.post("/products/find-match", data),
  uploadImage:   (id, file)  => {
    const fd = new FormData(); fd.append("file", file);
    // Delete Content-Type so axios sets multipart/form-data with correct boundary automatically
    return API.post(`/products/${id}/image`, fd, {
      headers: { "Content-Type": undefined }
    });
  },
  deleteImage:   (id)        => API.delete(`/products/${id}/image`),
  downloadTechPack: (id)      => API.get(`/products/${id}/tech-pack`, { responseType: "arraybuffer" }),
  uploadTechPack: (id, file)  => {
    const fd = new FormData(); fd.append("file", file);
    return API.post(`/products/${id}/tech-pack`, fd, {
      headers: { "Content-Type": undefined }
    });
  },
  deleteTechPack: (id)        => API.delete(`/products/${id}/tech-pack`),
};

export const transportApi = {
  getAll:    ()           => API.get("/transport"),
  getActive: ()           => API.get("/transport/active"),
  getById:   (id)         => API.get(`/transport/${id}`),
  create:    (data)       => API.post("/transport", data),
  update:    (id, data)   => API.put(`/transport/${id}`, data),
  delete:    (id)         => API.delete(`/transport/${id}`),
};
export const salesmanApi = {
  getAll:    ()           => API.get("/salesmen"),
  getActive: ()           => API.get("/salesmen/active"),
  getById:   (id)         => API.get(`/salesmen/${id}`),
  create:    (data)       => API.post("/salesmen", data),
  update:    (id, data)   => API.put(`/salesmen/${id}`, data),
  toggle:    (id)         => API.patch(`/salesmen/${id}/toggle`),
  delete:    (id)         => API.delete(`/salesmen/${id}`),
};

export const clothVariantApi = {
  getAll:         ()                           => API.get("/cloth-variants"),
  // Phase 5 — server-side pagination + sort + search.
  // params: { page, size, sortBy, sortDir, q, clothId, colorId, isActive }
  paged:          (params = {})                => API.get("/cloth-variants/paged", { params }),
  getByCloth:     (clothId)                    => API.get(`/cloth-variants/by-cloth/${clothId}`),
  getByClothPaged:(clothId, q="", page=0, size=20) =>
    API.get(`/cloth-variants/by-cloth/${clothId}/paged?q=${encodeURIComponent(q)}&page=${page}&size=${size}`),
  create:         (data)                       => API.post("/cloth-variants", data),
  update:         (id, data)                   => API.put(`/cloth-variants/${id}`, data),
  toggle:         (id)                         => API.patch(`/cloth-variants/${id}/toggle`),
  delete:         (id)                         => API.delete(`/cloth-variants/${id}`),
  setAllies:      (id, allyIds)                => API.put(`/cloth-variants/${id}/allies`, allyIds),
  // Phase 36da.71 — upload a variant photo and get back the storage
  // path.  The path is then sent on the next create/update as
  // imagePath so it persists on the variant row.  Cross-origin-safe
  // display via getFileUrl(imagePath).
  uploadImage:    (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return API.post("/cloth-variants/upload-image", fd, {
      headers: { "Content-Type": undefined }
    });
  },
};
export const serviceApi = {
  getAll:    ()         => API.get("/services"),
  getActive: ()         => API.get("/services/active"),
  create:    (data)     => API.post("/services", data),
  update:    (id, data) => API.put(`/services/${id}`, data),
  toggle:    (id)       => API.patch(`/services/${id}/toggle`),
  delete:    (id)       => API.delete(`/services/${id}`),
};

export const reasonApi = {
  ...makeCrud("reasons"),
  getActive: () => API.get("/reasons/active"),
};

export const hundekariApi = {
  getAll:    () => API.get("/hundekari"),
  getActive: () => API.get("/hundekari/active"),
  create:    (data) => API.post("/hundekari", data),
  update:    (id, data) => API.put(`/hundekari/${id}`, data),
  delete:    (id) => API.delete(`/hundekari/${id}`),
};